import {
  forwardRef,
  useId,
  type InputHTMLAttributes,
  type SelectHTMLAttributes,
} from "react";
import { cn } from "../../lib/cn.js";

interface FieldShell {
  label: string;
  error?: string | null;
  hint?: string;
  required?: boolean;
}

function describedBy(
  id: string,
  hint?: string,
  error?: string | null
): string | undefined {
  const ids = [hint ? `${id}-hint` : null, error ? `${id}-error` : null].filter(Boolean);
  return ids.length ? ids.join(" ") : undefined;
}

export interface TextInputProps
  extends InputHTMLAttributes<HTMLInputElement>, FieldShell {}

export const TextInput = forwardRef<HTMLInputElement, TextInputProps>(function TextInput(
  { label, error, hint, required, className, id: providedId, ...props },
  ref
) {
  const autoId = useId();
  const id = providedId ?? autoId;
  return (
    <div className="flex flex-col gap-1.5">
      <label
        htmlFor={id}
        className={cn(
          "text-sm font-medium text-ink",
          required && "after:ms-1 after:text-action-danger after:content-['*']"
        )}
      >
        {label}
      </label>
      <input
        ref={ref}
        id={id}
        required={required}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy(id, hint, error)}
        className={cn(
          "h-11 rounded-[var(--radius-card)] border bg-surface-raised px-3 text-sm text-ink placeholder:text-ink-muted",
          error ? "border-action-danger" : "border-line",
          className
        )}
        {...props}
      />
      {hint && !error ? (
        <p id={`${id}-hint`} className="text-xs text-ink-muted">
          {hint}
        </p>
      ) : null}
      {error ? (
        <p
          id={`${id}-error`}
          className="text-xs font-medium text-action-danger"
          role="alert"
        >
          {error}
        </p>
      ) : null}
    </div>
  );
});

export interface SelectInputProps
  extends SelectHTMLAttributes<HTMLSelectElement>, FieldShell {
  options: { value: string; label: string }[];
}

export function SelectInput({
  label,
  error,
  hint,
  required,
  options,
  id: providedId,
  className,
  ...props
}: SelectInputProps) {
  const autoId = useId();
  const id = providedId ?? autoId;
  return (
    <div className="flex flex-col gap-1.5">
      <label
        htmlFor={id}
        className={cn(
          "text-sm font-medium text-ink",
          required && "after:ms-1 after:text-action-danger after:content-['*']"
        )}
      >
        {label}
      </label>
      <select
        id={id}
        required={required}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy(id, hint, error)}
        className={cn(
          "h-11 rounded-[var(--radius-card)] border bg-surface-raised px-3 text-sm text-ink",
          error ? "border-action-danger" : "border-line",
          className
        )}
        {...props}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      {hint && !error ? (
        <p id={`${id}-hint`} className="text-xs text-ink-muted">
          {hint}
        </p>
      ) : null}
      {error ? (
        <p
          id={`${id}-error`}
          className="text-xs font-medium text-action-danger"
          role="alert"
        >
          {error}
        </p>
      ) : null}
    </div>
  );
}
